import { Component, OnInit } from '@angular/core';
import { TableService } from '../../services/table.service';
import { SkillInfo, TableItem } from '../../models/table';
import { TableModule } from 'primeng/table';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { Knowledge } from '../../models/enum/knowledge.enum';
import { Like } from '../../models/enum/like.enum';
import { Teaching } from '../../models/enum/teaching.enum';
import { Tooltip } from 'primeng/tooltip';
import { CommonModule, NgStyle } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ProgressSpinner } from 'primeng/progressspinner';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-skill-table',
  standalone: true,
  imports: [
    TableModule,
    MultiSelectModule,
    FormsModule,
    Tooltip,
    NgStyle,
    CommonModule,
    ProgressSpinner,
  ],
  templateUrl: './skill-table.component.html',
  styleUrls: ['./skill-table.component.scss'],
})
export class SkillTableComponent implements OnInit {
  skills: string[] = [];
  skillsCategory: string[] = [];
  filteredSkills: string[] = [];
  selectedSkills: string[] = [];

  levels: string[] = ['knowledge', 'like', 'teach', 'student'];
  selectedLevels: string[] = [];

  teams: string[] = [];
  selectedTeams: string[] = [];

  data: TableItem[] = [];
  filteredData: TableItem[] = [];

  knowledge = Knowledge;
  like = Like;
  teach = Teaching;

  groups: string[] = [];
  filteredSkillGroup: string[] = [];
  selectedSkillGroup: string[] = [];

  skillsWithGroup: { skillName: string; groupName: string }[] = [];

  myUsername: string | undefined;
  loading = true;

  constructor(
    private tableService: TableService,
    private router: Router,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loading = true;

    forkJoin({
      skills: this.tableService.getSkills(),
      data: this.tableService.getData(),
      user: this.http.get<{ username: string }>('/api/v1/me'),
    }).subscribe({
      next: ({ skills, data, user }) => {
        // --- skills ---
        const namedSkills = skills.map(skill => skill.skill_name);
        this.skills = namedSkills;
        this.skillsCategory = namedSkills;
        this.filteredSkills = namedSkills;
        this.groups = [...new Set(skills.map(skill => skill.g_name))];
        this.skillsWithGroup = skills.map(skill => ({
          skillName: skill.skill_name,
          groupName: skill.g_name,
        }));

        // --- data ---
        this.data = data;
        this.filteredData = [...data];
        this.teams = [...new Set(data.map(item => item.team))].sort((a, b) =>
          a.localeCompare(b)
        );

        // --- user ---
        this.myUsername = user.username;

        this.loading = false;
      },
      error: err => {
        console.error(err);
        this.loading = false;
      },
    });
  }

  filterColumnsByCategory() {
    this.filteredSkills = this.selectedSkills.length
      ? this.skills.filter(skill => this.selectedSkills.includes(skill))
      : this.skills;
    this.applyFilters();
  }

  filterColumnsByGroup() {
    this.filteredSkillGroup = this.selectedSkillGroup.length
      ? this.groups.filter(group => this.selectedSkillGroup.includes(group))
      : this.groups;

    if (this.selectedSkillGroup.length > 0) {
      this.filteredSkills = this.skillsWithGroup
        .filter(skill => this.selectedSkillGroup.includes(skill.groupName))
        .map(skill => skill.skillName);
    } else {
      this.filteredSkills = [...this.skills];
    }
    this.selectedSkills = [];
    this.skillsCategory = this.filteredSkills;
    this.applyFilters();
  }

  applyFilters() {
    this.filteredData = this.data.filter(item => {
      const matchesCategory =
        this.selectedSkills.length === 0 ||
        this.selectedSkills.some(skill => item.skills[skill]);

      const matchesLevel =
        this.selectedLevels.length === 0 ||
        this.selectedSkills.every(
          skill =>
            item.skills[skill] &&
            this.selectedLevels.every(level => {
              if (level === 'teach') {
                return item.skills[skill].teaching_learning === 0;
              } else if (level === 'student') {
                return item.skills[skill].teaching_learning === 1;
              } else {
                return (
                  item.skills[skill][level as keyof SkillInfo] !== null &&
                  item.skills[skill][level as keyof SkillInfo] === 1
                );
              }
            })
        );

      const matchesTeam =
        this.selectedTeams.length === 0 ||
        this.selectedTeams.includes(item.team);
      return matchesCategory && matchesLevel && matchesTeam;
    });
  }

  severityLevel(
    data: number,
    type: string
  ):
    | 'info'
    | 'success'
    | 'warn'
    | 'danger'
    | 'secondary'
    | 'contrast'
    | 'help'
    | 'primary' {
    if (data || data === 0) {
      switch (type) {
        case 'like':
        case 'knowledge':
          return data === 1 ? 'success' : data === 2 ? 'warn' : 'danger';
        case 'teaching_learning':
          return data === 0 ? 'success' : 'info';
        default:
          return 'secondary';
      }
    }
    return 'secondary';
  }

  getInitials(name: string): string {
    if (!name) return '';

    const parts = name.split(' ');
    const initials = (parts[0]?.charAt(0) || '') + (parts[1]?.charAt(0) || '');

    return initials.toUpperCase();
  }

  getIcon(
    category: 'knowledge' | 'like' | 'teaching_learning',
    value: number | null
  ): string {
    const icons: Record<
      'knowledge' | 'like' | 'teaching_learning',
      Record<string, string>
    > = {
      knowledge: {
        '1': 'fa-check',
        '2': 'fa-xmark',
        '3': 'fa-minus',
      },
      like: {
        '1': 'fa-check',
        '2': 'fa-xmark',
        '3': 'fa-minus',
      },
      teaching_learning: {
        '0': 'fa-person-chalkboard',
        '1': 'fa-graduation-cap',
        '2': 'fa-minus',
      },
    };

    return icons[category]?.[String(value)] || 'fa-solid fa-minus';
  }

  getColor(
    category: 'knowledge' | 'like' | 'teaching_learning',
    value: number | null
  ): string {
    const colors: Record<
      'knowledge' | 'like' | 'teaching_learning',
      Record<string, string>
    > = {
      knowledge: {
        '1': 'p-badge-success',
        '2': 'p-badge-error',
        '3': 'p-badge-undefined',
      },
      like: {
        '1': 'p-badge-success',
        '2': 'p-badge-error',
        '3': 'p-badge-undefined',
      },
      teaching_learning: {
        '0': 'p-badge-info',
        '1': 'p-badge-info',
        '2': 'p-badge-undefined',
      },
    };

    return colors[category]?.[String(value)] || 'p-badge-undefined';
  }

  getTooltipLabel(
    category: 'knowledge' | 'like' | 'teaching_learning',
    value: number | null
  ): string {
    const colors: Record<
      'knowledge' | 'like' | 'teaching_learning',
      Record<string, string>
    > = {
      knowledge: {
        '1': 'Conosco',
        '2': 'Non conosco',
        '3': 'Non lo so',
      },
      like: {
        '1': 'Interessato',
        '2': 'Non interessato',
        '3': 'Non lo so',
      },
      teaching_learning: {
        '0': 'Disponibile come mentore',
        '1': 'Desideroso di imparare',
        '2': 'Indifferente',
      },
    };
    return colors[category]?.[String(value)] || 'Non definito';
  }

  openUserSMDetail(userId: any) {
    // const url = this.router.serializeUrl(
    //   this.router.createUrlTree(['/detail-user'], {
    //     queryParams: { userId },
    //   })
    // );
    //
    // const baseUrl = window.location.origin + '/skillmap';
    this.router.navigate(['/skillmap/detail-user'], {
      queryParams: { userId },
    });
  }
}
